/**
 * Every read `/raporty` makes, as `queryOptions`.
 *
 * No repository and no service: TanStack Query already owns async state, and `CLAUDE.md` puts query
 * keys and options in the slice's `api/` segment.
 *
 * `reportsRoot` is the blanket. Every key below descends from it, so a write whose blast radius is
 * genuinely "the whole screen" — the editor saving a form, the matrix toggling an assignment —
 * invalidates one prefix rather than enumerating four. Blocks B and C add their keys under it.
 *
 * ── The roster is read through `entities/client`'s OWN options ───────────────────────────────
 * `rosterQueryOptions` and its key, with a `select` of this screen's own on top. TanStack keys the
 * cache ENTRY and each `useQuery` projects from it, so the roster this screen needs for faces and
 * for the client facet is the same response the clients panel already holds — one request, two
 * projections. A key of this slice's own would put a second copy of the trainer's whole roster in
 * the cache and let the two drift apart the moment one was invalidated.
 *
 * ── The window is five minutes, and it is the read-only window ───────────────────────────────
 * The same figure the plan library's inspector takes, for the same reason: this screen owns nothing
 * it is looking at. The editor (block C) writes, and it invalidates rather than shortening a window.
 */

import { queryOptions } from '@tanstack/react-query';

import { buildClientRoster, rosterQueryOptions } from '~/entities/client';
import { surveyTemplatesCacheRoot } from '~/features/survey-question-editor';
import {
  getSurveyReviewQueue,
  getSurveyTemplate,
  getSurveyTemplateId,
} from '~/shared/api/generated/endpoints/survey/survey';

import { clientFaces, type ClientFace } from '../model/clientFaces';

/**
 * The prefix every read on this screen shares. Invalidate it to drop the whole screen's cache. It is
 * the survey editor's own root, because the public form builder's save must reach it too.
 */
export const reportsRoot = surveyTemplatesCacheRoot;

/** The owner's window for a read-only preview, 3 September 2026. */
const REPORTS_STALE_TIME = 5 * 60 * 1000;

/**
 * `GET /survey/templates` — every form the trainer has. `entities/survey-template`'s since the client
 * card's „Przypisz ankietę" reads it too; named here so this screen's reads stay one import.
 */
export { surveyTemplatesQueryKey, surveyTemplatesQueryOptions } from '~/entities/survey-template';

export const intakeTemplateQueryKey = [...reportsRoot, 'intake'] as const;

/**
 * `GET /survey/template` — the intake, whole, with its items.
 *
 * A route of its own rather than `/survey/template/{id}`: the intake is the trainer's single
 * onboarding form and the backend resolves it without being told which one, which is also how a
 * trainer who has never touched it gets the system default rather than a 404.
 */
export function intakeTemplateQueryOptions() {
  return queryOptions({
    queryKey: intakeTemplateQueryKey,
    queryFn: ({ signal }) => getSurveyTemplate(undefined, signal),
    staleTime: REPORTS_STALE_TIME,
  });
}

export function surveyTemplateQueryKey(id: number) {
  return [...reportsRoot, 'template', id] as const;
}

/**
 * `GET /survey/template/{id}` — one check-in, whole.
 *
 * `enabled` rather than a nullable id inside the function: a query that is off is `isPending` for
 * ever, which is exactly right here — nothing is selected, so nothing is waiting — and the screen's
 * loading gate carries the enablement predicate with it rather than reading `isPending` alone.
 */
export function surveyTemplateQueryOptions(id: number | null) {
  return queryOptions({
    queryKey: surveyTemplateQueryKey(id ?? 0),
    queryFn: ({ signal }) => getSurveyTemplateId(id ?? 0, undefined, signal),
    enabled: id !== null,
    staleTime: REPORTS_STALE_TIME,
  });
}

/**
 * `GET /survey/review-queue` — everything submitted and not yet read, newest first.
 *
 * ONE key, because there is one queue. The read took a `kind` parameter and had a key per filter
 * until 17 September 2026, when the owner removed the three-way segment above the list („Wszystkie /
 * Check-iny / Wstępne"); the request now names no kind at all, so the backend answers with every
 * submission. The prefix a review invalidated was a prefix for that reason and is now simply the key.
 *
 * `staleTime: 0`. Unlike everything else on this screen, this is a worklist the trainer acts on and
 * the phone writes to — a client submitting a check-in while the tab is open adds a row, and a five
 * minute window would hold the old list across a tab switch. The read-only window above is for a
 * shelf of forms that changes when the trainer changes it.
 */
export const reviewQueueQueryKey = [...reportsRoot, 'reviewQueue'] as const;

export function reviewQueueQueryOptions() {
  return queryOptions({
    queryKey: reviewQueueQueryKey,
    queryFn: ({ signal }) => getSurveyReviewQueue(undefined, undefined, signal),
    staleTime: 0,
  });
}

/**
 * The standard catalog per survey kind. Defined once, in the editor feature both hosts share; named
 * here so this screen's reads stay one import.
 */
export { surveyCatalogQueryKey, surveyCatalogQueryOptions } from '~/features/survey-question-editor';

/**
 * The roster, projected to the faces this screen draws. One request, shared with the clients panel.
 *
 * `buildClientRoster` is handed NO attention queue, and that is not a shortcut: its second argument
 * decides which tasks hang off a row, and this screen draws a name and a photograph. Asking for the
 * queue as well would be a second request for a fact no card prints.
 */
export function clientFacesQueryOptions() {
  return queryOptions({
    ...rosterQueryOptions(),
    select: (customers): ReadonlyMap<number, ClientFace> =>
      clientFaces(buildClientRoster(customers, undefined)),
  });
}
